import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useCountry } from "../context/CountryContext";

export default function PalmPaySendPalmPayPage() {
  const { isGhana, config } = useCountry();
  const API_BASE = "https://alkebulanfkalertbackend.onrender.com/api";

  // 🎨 THEME — locked to user's country
  const theme = isGhana
    ? {
        countryCode: "GH",
        flag: "🇬🇭",
        currencySymbol: "₵",
        digits: 10,
        title: "Send Cedi Wallet",
        defaultBank: { value: "MTN", label: "📱 MTN Mobile Money" },
        bgGradient: "from-red-900 via-rose-900 to-black",
        headerGradient: "from-yellow-500 to-red-600",
        primaryGradient: "from-yellow-500 to-red-600",
        primaryGradientHover: "from-yellow-600 to-red-700",
        focusBorder: "focus:border-yellow-500",
        receiptCircleColor: "text-yellow-400",
        receiptAccentColor: "text-yellow-400",
        doneBtn: "bg-yellow-500 hover:bg-yellow-600 text-black",
        accentText: "text-yellow-400",
      }
    : {
        countryCode: "NG",
        flag: "🇳🇬",
        currencySymbol: "₦",
        digits: 10,
        title: "Send to PalmPay",
        defaultBank: { value: "999991", label: "PalmPay" },
        bgGradient: "from-purple-900 via-purple-800 to-black",
        headerGradient: "from-pink-600 to-purple-600",
        primaryGradient: "from-pink-600 to-purple-600",
        primaryGradientHover: "from-pink-700 to-purple-700",
        focusBorder: "focus:border-pink-500",
        receiptCircleColor: "text-green-400",
        receiptAccentColor: "text-pink-400",
        doneBtn: "bg-pink-600 hover:bg-pink-700 text-white",
        accentText: "text-pink-400",
      };

  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(theme.defaultBank);
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form");
  const [txnId, setTxnId] = useState(null);
  const [txnTime, setTxnTime] = useState(null);
  const [error, setError] = useState(null);
  const [accountLockError, setAccountLockError] = useState(null);

  // ✅ Fetch bank list — locked to user's country
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const res = await fetch(`${API_BASE}/bank/list?country=${theme.countryCode}`);
        const data = await res.json();
        const options = Array.isArray(data)
          ? data.map((bank) => ({
              value: bank.code,
              label: bank.name,
            }))
          : [];
        setBanks([theme.defaultBank, ...options.filter((o) => o.value !== theme.defaultBank.value)]);
      } catch (err) {
        console.error("⚠️ Error fetching banks:", err);
        setBanks([theme.defaultBank]);
      }
    };
    fetchBanks();
  }, [theme.countryCode]);

  // Reset selected bank when country changes
  useEffect(() => {
    setSelectedBank(theme.defaultBank);
  }, [theme.countryCode]);

  // ✅ Auto verify account
  useEffect(() => {
    const verifyAccount = async () => {
      if (accountNumber.length === theme.digits && selectedBank) {
        setLoading(true);
        setAccountName("");
        try {
          const res = await fetch(`${API_BASE}/bank/resolve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              account_number: accountNumber,
              bank_code: selectedBank.value,
              country: theme.countryCode,
            }),
          });

          const data = await res.json();
          const name =
            data?.account_name ||
            data?.data?.account_name ||
            data?.data?.data?.account_name;

          if (name) {
            setAccountName(name);
          } else {
            setAccountName("❌ Could not verify account");
          }
        } catch (err) {
          console.error("⚠️ Error verifying account:", err);
          setAccountName("⚠️ Error verifying account");
        } finally {
          setLoading(false);
        }
      } else {
        setAccountName("");
      }
    };
    verifyAccount();
  }, [accountNumber, selectedBank, theme.countryCode, theme.digits]);

  const handleContinue = () => {
    if (!accountNumber || !amount) {
      setError("Please fill all fields");
      return;
    }
    if (!accountName || accountName.startsWith("❌") || accountName.startsWith("⚠️")) {
      setError("Account not verified");
      return;
    }
    setStep("confirm");
  };

  // ✅ Send transfer
  const handleSend = async () => {
    const user = JSON.parse(localStorage.getItem("user"));

    // ✅ Check if account is locked LOCALLY first
    if (user?.accountLocked) {
      setAccountLockError(
        user?.accountLockReason ||
          "Your account has been temporarily locked. Please contact support."
      );
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/bank/wallet/transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          account_number: accountNumber,
          bank_code: selectedBank.value,
          account_name: accountName,
          amount,
          country: theme.countryCode,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setTxnId(data?.transaction?.transactionId || "TXN-" + Date.now());
        setTxnTime(new Date().toLocaleString());
        setStep("receipt");
      } else {
        // ✅ Check if it's an account lock from backend
        if (
          res.status === 403 ||
          data.accountLocked ||
          (data.message && data.message.toLowerCase().includes("lock"))
        ) {
          setAccountLockError(
            data.message ||
              data.error ||
              "Your account has been temporarily locked. Please contact support."
          );
        } else {
          setError(data.error || data.message || "Transfer failed");
        }
      }
    } catch (err) {
      console.error("⚠️ Transfer error:", err);
      setError("Transfer failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b ${theme.bgGradient} flex items-center justify-center px-4 py-8`}>

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      {/* 🔒 ACCOUNT LOCKED Modal */}
      {accountLockError && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60">
          <div className="w-full max-w-md bg-gray-900 rounded-t-3xl p-6 animate-slideUp">
            <div className="w-12 h-1.5 bg-gray-600 rounded-full mx-auto mb-4"></div>

            <div className="flex justify-center mb-3">
              <div className="bg-red-600 p-3 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="white"
                  viewBox="0 0 24 24"
                  className="w-7 h-7"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 1a4 4 0 014 4v3h1a3 3 0 013 3v9a3 3 0 01-3 3H7a3 3 0 01-3-3v-9a3 3 0 013-3h1V5a4 4 0 014-4zm0 2a2 2 0 00-2 2v3h4V5a2 2 0 00-2-2z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>

            <h2 className="text-center text-lg font-bold mb-2 text-white">🔒 Account Locked</h2>
            <p className="text-center text-gray-300 text-sm mb-3">
              You cannot perform this transfer at the moment.
            </p>

            <div className="bg-red-500/10 border border-red-400/30 rounded-xl p-3 mb-5">
              <p className="text-center text-red-300 text-sm font-medium">Reason:</p>
              <p className="text-center text-red-200 text-sm mt-1">{accountLockError}</p>
            </div>

            <button
              onClick={() => setAccountLockError(null)}
              className={`w-full bg-gradient-to-r ${theme.primaryGradient} py-3 rounded-xl font-semibold text-white transition`}
            >
              I Understand
            </button>

            <p className="text-center text-xs text-gray-500 mt-3">
              Contact support if you need help
            </p>
          </div>
        </div>
      )}

      {/* ⚠️ General Error Modal */}
      {error && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60">
          <div className="w-full max-w-md bg-gray-900 rounded-t-3xl p-6 animate-slideUp">
            <div className="w-12 h-1.5 bg-gray-600 rounded-full mx-auto mb-4"></div>
            <div className="flex justify-center mb-3">
              <div className="bg-red-600 p-3 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 24 24" className="w-6 h-6">
                  <path fillRule="evenodd" d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 13h-2v2h2v-2zm0-8h-2v6h2V7z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <h2 className="text-center text-lg font-semibold mb-1 text-white">Transaction Failed</h2>
            <p className="text-center text-red-400 text-sm mb-5">{error}</p>
            <button onClick={() => setError(null)} className="w-full bg-red-600 hover:bg-red-700 py-3 rounded-xl font-semibold text-white">
              Try Again
            </button>
          </div>
        </div>
      )}

      <div className="w-full max-w-md bg-[#1c1c24] rounded-2xl shadow-2xl p-6 relative overflow-hidden">
        {/* Header Gradient */}
        <div className={`absolute top-0 left-0 right-0 h-28 bg-gradient-to-r ${theme.headerGradient} rounded-t-2xl`}></div>

        <div className="relative z-10 mb-8">
          <h1 className="text-2xl font-bold text-white text-center mt-6">
            {theme.title}
          </h1>
          <p className="text-center text-white/70 text-xs mt-1">
            {theme.flag} {config.name} only
          </p>
        </div>

        {/* STEP 1 */}
        {step === "form" && (
          <>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-300">
                Select Bank
              </label>
              <Select
                value={selectedBank}
                onChange={setSelectedBank}
                options={banks}
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "#111",
                    borderColor: "#444",
                    color: "white",
                  }),
                  singleValue: (base) => ({ ...base, color: "white" }),
                  menu: (base) => ({ ...base, backgroundColor: "#111" }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? "#374151" : "#111",
                    color: "white",
                  }),
                }}
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-300">
                {isGhana ? "Mobile Number / Account" : "Account Number"}
              </label>
              <input
                type="number"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                maxLength={theme.digits}
                className={`w-full p-3 rounded-lg bg-gray-900 text-white outline-none border border-gray-700 ${theme.focusBorder} transition`}
                placeholder={`Enter ${theme.digits}-digit number`}
              />
            </div>

            {loading ? (
              <p className="text-yellow-400 text-sm mb-4">🔄 Verifying account...</p>
            ) : accountName ? (
              <p
                className={`mb-4 text-sm px-3 py-2 rounded-lg ${
                  accountName.startsWith("❌") || accountName.startsWith("⚠️")
                    ? "bg-red-500/20 text-red-400 border border-red-500/40"
                    : "bg-green-500/20 text-green-400 border border-green-500/40"
                }`}
              >
                {accountName}
              </p>
            ) : null}

            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-gray-300">
                Amount ({theme.currencySymbol})
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`w-full p-3 rounded-lg bg-gray-900 text-white outline-none border border-gray-700 ${theme.focusBorder} transition`}
                placeholder="Enter amount"
              />
            </div>

            <button
              onClick={handleContinue}
              disabled={
                loading ||
                !accountNumber ||
                !amount ||
                !accountName ||
                accountName.startsWith("❌") ||
                accountName.startsWith("⚠️")
              }
              className={`w-full py-3 rounded-lg font-semibold text-white shadow-lg transition transform hover:scale-[1.02] ${
                loading ||
                !accountNumber ||
                !amount ||
                !accountName ||
                accountName.startsWith("❌") ||
                accountName.startsWith("⚠️")
                  ? "bg-gray-600 cursor-not-allowed"
                  : `bg-gradient-to-r ${theme.primaryGradient} hover:${theme.primaryGradientHover}`
              }`}
            >
              Continue →
            </button>
          </>
        )}

        {/* CONFIRM */}
        {step === "confirm" && (
          <div className="text-white">
            <h3 className="text-lg font-semibold mb-4">Confirm Transfer</h3>
            <p className="mb-2">
              Country: <span className={theme.accentText}>{theme.flag} {config.name}</span>
            </p>
            <p className="mb-2">
              Account Name: <span className="text-green-400">{accountName}</span>
            </p>
            <p className="mb-2">
              Account Number: <span className="text-green-400">{accountNumber}</span>
            </p>
            <p className="mb-2">
              Bank: <span className="text-green-400">{selectedBank.label}</span>
            </p>
            <p className="mb-2">
              Amount:{" "}
              <span className="text-green-400">
                {theme.currencySymbol}{Number(amount).toLocaleString()}
              </span>
            </p>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setStep("form")}
                className="flex-1 bg-gray-700 py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={loading}
                className={`flex-1 bg-gradient-to-r ${theme.primaryGradient} hover:${theme.primaryGradientHover} py-3 rounded-xl`}
              >
                {loading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        )}

        {/* RECEIPT */}
        {step === "receipt" && (
          <div className="text-center text-white">
            <h3 className={`text-xl font-bold ${theme.receiptCircleColor} mb-4`}>
              ✅ Transfer Successful!
            </h3>
            <p className="mb-2">
              Sent {theme.currencySymbol}{Number(amount).toLocaleString()} to{" "}
              <span className={theme.receiptAccentColor}>{accountName}</span>
            </p>
            <p className="mb-2">Account: {accountNumber}</p>
            <p className="mb-2">Bank: {selectedBank.label}</p>
            <p className="mb-2">Country: {theme.flag} {config.name}</p>
            <p className="mb-2">Transaction ID: {txnId}</p>
            <p className="mb-6">Date: {txnTime}</p>
            <button
              onClick={() => {
                setAccountNumber("");
                setAmount("");
                setAccountName("");
                setStep("form");
              }}
              className={`w-full py-3 rounded-lg ${theme.doneBtn} font-semibold`}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
